import React, { useEffect, useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import { setPageTitle } from "../../../redux/store/themeConfigSlice"
import { chooseFile } from "../../../components/ReadCSVFile"
import { TAcreateUser } from "../../../services/userAPI"
import { getErrorMessage, getErrorStatus } from "../../../types/error"

type UserRow = {
  id: string
  phone: string
  insta_username: string
  tiktok_username: string
}

type CreateResult = {
  id: string
  phone: string
  insta_username: string
  tiktok_username: string
  status: "success" | "error"
  message?: string
}

const createId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`

const emptyRow = (): UserRow => ({
  id: createId(),
  phone: "",
  insta_username: "",
  tiktok_username: "",
})

const normalizeKey = (key: string) => key.toLowerCase().replace(/[^a-z0-9]/g, "")

const getRowValue = (row: Record<string, unknown>, keys: string[]) => {
  const normalizedMap = Object.entries(row).reduce<Record<string, unknown>>((acc, [k, v]) => {
    acc[normalizeKey(k)] = v
    return acc
  }, {})

  for (const key of keys) {
    const normalizedKey = normalizeKey(key)
    const directValue = row[key]
    if (directValue !== undefined && directValue !== null) {
      return String(directValue).trim()
    }
    if (normalizedMap[normalizedKey] !== undefined && normalizedMap[normalizedKey] !== null) {
      return String(normalizedMap[normalizedKey]).trim()
    }
  }
  return ""
}

const TabButton = ({
  isActive,
  onClick,
  label,
}: {
  isActive: boolean
  onClick: () => void
  label: string
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
      isActive ? "bg-primary text-white shadow" : "bg-white text-gray-600 hover:text-primary"
    }`}
  >
    {label}
  </button>
)

const SectionCard = ({
  title,
  description,
  children,
}: {
  title: string
  description: string
  children: React.ReactNode
}) => (
  <div className="panel">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
    </div>
    {children}
  </div>
)

const CreateUser = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<"manual" | "excel">("manual")
  const [manualRows, setManualRows] = useState<UserRow[]>([emptyRow()])
  const [excelRows, setExcelRows] = useState<UserRow[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [progress, setProgress] = useState({ current: 0, total: 0 })
  const [results, setResults] = useState<CreateResult[]>([])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [currentCreatingId, setCurrentCreatingId] = useState<string | null>(null)
  const [aiAnalysis, setAiAnalysis] = useState(false)

  useEffect(() => {
    dispatch(setPageTitle("Kullanıcı Oluştur"))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const manualValidCount = useMemo(
    () => manualRows.filter((row) => row.phone || row.insta_username || row.tiktok_username).length,
    [manualRows],
  )

  const excelValidCount = useMemo(
    () => excelRows.filter((row) => row.phone || row.insta_username || row.tiktok_username).length,
    [excelRows],
  )

  const updateManualRow = (id: string, key: keyof UserRow, value: string) => {
    setManualRows((prev) => prev.map((row) => (row.id === id ? { ...row, [key]: value } : row)))
  }

  const removeManualRow = (id: string) => {
    setManualRows((prev) => (prev.length > 1 ? prev.filter((row) => row.id !== id) : prev))
  }

  const clearResults = () => {
    setResults([])
    setErrorMessage(null)
    setProgress({ current: 0, total: 0 })
  }

  const handleCreateUsers = async (rows: UserRow[], sourceLabel: string) => {
    clearResults()
    const validRows = rows.filter((row) => row.phone || row.insta_username || row.tiktok_username)
    if (validRows.length === 0) {
      setErrorMessage("Lütfen en az bir satır için telefon, IG veya TikTok kullanıcı adı girin.")
      return
    }

    setIsSubmitting(true)
    setProgress({ current: 0, total: validRows.length })

    const nextResults: CreateResult[] = []
    for (let index = 0; index < validRows.length; index += 1) {
      const row = validRows[index]
      setCurrentCreatingId(row.id)
      try {
        await TAcreateUser({
          phone: row.phone || undefined,
          insta_username: row.insta_username || undefined,
          tiktok_username: row.tiktok_username || undefined,
          ai_analysis: aiAnalysis,
        })
        nextResults.push({
          id: row.id,
          phone: row.phone,
          insta_username: row.insta_username,
          tiktok_username: row.tiktok_username,
          status: "success",
        })
      } catch (error: unknown) {
        if (getErrorStatus(error) === 401) {
          setIsSubmitting(false)
          setCurrentCreatingId(null)
          navigate("/login")
          return
        }
        nextResults.push({
          id: row.id,
          phone: row.phone,
          insta_username: row.insta_username,
          tiktok_username: row.tiktok_username,
          status: "error",
          message: getErrorMessage(error),
        })
      } finally {
        setProgress({ current: index + 1, total: validRows.length })
      }
    }

    setResults(nextResults)
    setIsSubmitting(false)
    setCurrentCreatingId(null)
    if (nextResults.every((result) => result.status === "success")) {
      setErrorMessage(null)
    } else {
      setErrorMessage(`${sourceLabel} içinde bazı satırlar oluşturulamadı.`)
    }
  }

  const handleSelectExcel = async () => {
    clearResults()
    try {
      const rows = await chooseFile()
      const parsedRows: UserRow[] = rows.map((row: Record<string, unknown>) => {
        const phone = getRowValue(row, [
          "phone",
          "telefon",
          "tel",
          "gsm",
          "telefon_no",
          "telefon_numarasi",
          "phone_number",
        ])
        const insta_username = getRowValue(row, [
          "insta_username",
          "insta username",
          "insta.username",
          "instagram_username",
          "instagram username",
          "instagram",
          "ig",
          "insta",
        ])
        const tiktok_username = getRowValue(row, [
          "tiktok_username",
          "tiktok username",
          "tiktok.username",
          "tiktok",
          "tt",
        ])
        return {
          id: createId(),
          phone,
          insta_username,
          tiktok_username,
        }
      })
      setExcelRows(parsedRows)
    } catch (error) {
      setErrorMessage(getErrorMessage(error))
    }
  }

  const handleDownloadTemplate = () => {
    const header = ["phone", "insta_username", "tiktok_username"].join(",")
    const example = ["+905xxxxxxxxx", "teamfluencer", "teamfluencer_tt"].join(",")
    const csvContent = `${header}\n${example}\n`
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "create_user_template.csv"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-6">
      <div className="panel bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Kullanıcı Oluştur</h1>
            <p className="text-sm text-white/80 mt-1">
              Excel veya manuel giriş ile hızlıca kullanıcı oluşturabilirsiniz.
            </p>
          </div>
          <div className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 text-sm">
            <span>Toplam Başarı</span>
            <span className="font-semibold">
              {results.filter((result) => result.status === "success").length}
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <TabButton
          isActive={activeTab === "manual"}
          onClick={() => setActiveTab("manual")}
          label="Manuel Giriş"
        />
        <TabButton
          isActive={activeTab === "excel"}
          onClick={() => setActiveTab("excel")}
          label="Excel/CSV Yükleme"
        />
      </div>

      {activeTab === "manual" && (
        <SectionCard
          title="Manuel Kullanıcı Girişi"
          description="IG veya TikTok kullanıcı adı girerek kullanıcı oluşturun."
        >
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-left text-gray-500">
                  <tr className="border-b">
                    <th className="py-2 px-2 w-8"></th>
                    <th className="py-2 px-2">#</th>
                    <th className="py-2 px-2">IG Kullanıcı Adı</th>
                    <th className="py-2 px-2">TikTok Kullanıcı Adı</th>
                    <th className="py-2 px-2">Telefon(Opsiyonel)</th>
                    <th className="py-2 px-2 text-right">Aksiyon</th>
                  </tr>
                </thead>
                <tbody>
                  {manualRows.map((row, index) => (
                    <tr key={row.id} className="border-b last:border-b-0">
                      <td className="py-2 px-2">
                        {currentCreatingId === row.id && (
                          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                        )}
                      </td>
                      <td className="py-2 px-2 text-gray-400">{index + 1}</td>
                      <td className="py-2 px-2">
                        <input
                          type="text"
                          placeholder="ornekuser"
                          className="form-input text-sm"
                          value={row.insta_username}
                          onChange={(e) =>
                            updateManualRow(row.id, "insta_username", e.target.value)
                          }
                        />
                      </td>
                      <td className="py-2 px-2">
                        <input
                          type="text"
                          placeholder="ornekuser_tt"
                          className="form-input text-sm"
                          value={row.tiktok_username}
                          onChange={(e) =>
                            updateManualRow(row.id, "tiktok_username", e.target.value)
                          }
                        />
                      </td>
                      <td className="py-2 px-2">
                        <input
                          type="text"
                          placeholder="+905xxxxxxxxx"
                          className="form-input text-sm"
                          value={row.phone}
                          onChange={(e) => updateManualRow(row.id, "phone", e.target.value)}
                        />
                      </td>
                      <td className="py-2 px-2 text-right">
                        <button
                          type="button"
                          className="text-xs text-red-500 hover:text-red-600"
                          onClick={() => removeManualRow(row.id)}
                        >
                          Sil
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => setManualRows((prev) => [...prev, emptyRow()])}
              >
                Yeni Satır Ekle
              </button>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span>Geçerli Satır:</span>
                <span className="font-semibold">{manualValidCount}</span>
              </div>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => setAiAnalysis((prev) => !prev)}
              >
                AI Analiz: {aiAnalysis ? "Açık" : "Kapalı"}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={isSubmitting}
                onClick={() => handleCreateUsers(manualRows, "Manuel giriş")}
              >
                {isSubmitting ? "Oluşturuluyor..." : "Kullanıcıları Oluştur"}
              </button>
            </div>
          </div>
        </SectionCard>
      )}

      {activeTab === "excel" && (
        <SectionCard
          title="Excel/CSV ile Toplu Oluşturma"
          description="Şablonu indirip kullanıcı listesi yükleyebilirsiniz."
        >
          <div className="space-y-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={handleSelectExcel}
                >
                  Dosya Seç
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={handleDownloadTemplate}
                >
                  Örnek Şablon İndir
                </button>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span>Geçerli Satır:</span>
                <span className="font-semibold">{excelValidCount}</span>
              </div>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => setAiAnalysis((prev) => !prev)}
              >
                AI Analiz: {aiAnalysis ? "Açık" : "Kapalı"}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={isSubmitting || excelRows.length === 0}
                onClick={() => handleCreateUsers(excelRows, "Excel yükleme")}
              >
                {isSubmitting ? "Oluşturuluyor..." : "Toplu Oluştur"}
              </button>
            </div>

            {excelRows.length > 0 ? (
              <div className="overflow-x-auto border rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="text-left text-gray-500 bg-gray-50">
                    <tr>
                      <th className="py-2 px-2 w-8"></th>
                      <th className="py-2 px-2">#</th>
                      <th className="py-2 px-2">IG Kullanıcı Adı</th>
                      <th className="py-2 px-2">TikTok Kullanıcı Adı</th>
                      <th className="py-2 px-2">Telefon</th>
                      <th className="py-2 px-2 text-right">Aksiyon</th>
                    </tr>
                  </thead>
                  <tbody>
                    {excelRows.slice(0, 50).map((row, index) => (
                      <tr key={row.id} className="border-b last:border-b-0">
                        <td className="py-2 px-2">
                          {currentCreatingId === row.id && (
                            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                          )}
                        </td>
                        <td className="py-2 px-2 text-gray-400">{index + 1}</td>
                        <td className="py-2 px-2">{row.insta_username || "-"}</td>
                        <td className="py-2 px-2">{row.tiktok_username || "-"}</td>
                        <td className="py-2 px-2">{row.phone || "-"}</td>
                        <td className="py-2 px-2 text-right">
                          <button
                            type="button"
                            className="text-xs text-red-500 hover:text-red-600"
                            onClick={() =>
                              setExcelRows((prev) => prev.filter((item) => item.id !== row.id))
                            }
                          >
                            Sil
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {excelRows.length > 50 && (
                  <div className="p-3 text-xs text-gray-500">
                    İlk 50 satır gösteriliyor. Toplam: {excelRows.length} satır.
                  </div>
                )}
              </div>
            ) : (
              <div className="border border-dashed rounded-lg p-6 text-center text-sm text-gray-500">
                Henüz dosya seçilmedi. Şablon ile Excel veya CSV yükleyebilirsiniz.
              </div>
            )}
          </div>
        </SectionCard>
      )}

      {(errorMessage || results.length > 0) && (
        <SectionCard
          title="İşlem Durumu"
          description="Oluşturma sonuçlarını aşağıda görebilirsiniz."
        >
          <div className="space-y-3">
            {errorMessage && <div className="text-sm text-red-500">{errorMessage}</div>}
            {progress.total > 0 && (
              <div className="text-sm text-gray-500">
                İlerleme: {progress.current}/{progress.total}
              </div>
            )}
            {results.length > 0 && (
              <div className="overflow-x-auto border rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="text-left text-gray-500 bg-gray-50">
                    <tr>
                      <th className="py-2 px-2">IG Kullanıcı Adı</th>
                      <th className="py-2 px-2">TikTok Kullanıcı Adı</th>
                      <th className="py-2 px-2">Telefon</th>
                      <th className="py-2 px-2">Durum</th>
                      <th className="py-2 px-2">Mesaj</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((result) => (
                      <tr key={result.id} className="border-b last:border-b-0">
                        <td className="py-2 px-2">{result.insta_username || "-"}</td>
                        <td className="py-2 px-2">{result.tiktok_username || "-"}</td>
                        <td className="py-2 px-2">{result.phone || "-"}</td>
                        <td
                          className={`py-2 px-2 font-semibold ${
                            result.status === "success" ? "text-green-600" : "text-red-600"
                          }`}
                        >
                          {result.status === "success" ? "Başarılı" : "Hatalı"}
                        </td>
                        <td className="py-2 px-2 text-xs text-gray-500">{result.message || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </SectionCard>
      )}
    </div>
  )
}

export default CreateUser
